import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Check, ArrowRight } from 'lucide-react';

const LEAD_API_URL = 'https://api.worksuite.cl/v1/crm-capture-lead';
const LEAD_API_KEY = 'sb_publishable_pikt-oExOFtm-ZaozkAwaQ_qKqqyb91';
const LEAD_ORG_ID = '69d8e0bb-ed36-43c1-9416-d794e7f75ee0';

const INITIAL = { name: '', email: '', company: '' };

const LeadModal = ({ open, onOpenChange, sourceDetail = 'Landing principal - Modal Header' }) => {
  const [formData, setFormData] = useState(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch(LEAD_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: LEAD_API_KEY
        },
        body: JSON.stringify({
          ...formData,
          org_id: LEAD_ORG_ID,
          source: 'webhook',
          source_detail: sourceDetail
        })
      });

      if (!res.ok) throw new Error('Bad response');

      setStatus('success');
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion_event_submit_lead_form');
      }
      setFormData(INITIAL);
    } catch (err) {
      console.error(err);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      // reset status when modal closes so re-opening feels fresh
      setTimeout(() => setStatus(null), 300);
    }
    onOpenChange(nextOpen);
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="lead-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Solicita tu demo gratuita
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Déjanos tus datos y un ejecutivo te contacta en menos de 24 horas hábiles.
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="py-6" data-testid="lead-modal-success">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-xl font-semibold text-gray-900">¡Gracias! Te contactaremos pronto.</p>
              <p className="text-sm text-gray-600">
                Revisamos tu solicitud y agendamos la demo por email o WhatsApp.
              </p>
              <Button
                className="mt-4 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleClose(false)}
                data-testid="lead-modal-close"
              >
                Cerrar
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="lead-modal-form">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Nombre completo *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={isSubmitting}
                className={inputClass}
                placeholder="Juan Pérez"
                data-testid="lead-modal-name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
                className={inputClass}
                placeholder="juan@empresa.cl"
                data-testid="lead-modal-email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Empresa</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                disabled={isSubmitting}
                className={inputClass}
                placeholder="Mi Empresa S.A."
                data-testid="lead-modal-company"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="lead-modal-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  Solicitar Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            {status === 'error' && (
              <p className="text-sm text-red-600 text-center" data-testid="lead-modal-error">
                Ocurrió un error. Intenta nuevamente o escríbenos a soporte@cloudbase.cl.
              </p>
            )}

            <p className="text-xs text-gray-500 text-center">
              ¿Prefieres el formulario completo?{' '}
              <a href="/demo" className="text-green-600 hover:underline">
                Ir a /demo
              </a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadModal;
